import React from 'react';
import { useTranslation } from 'react-i18next';

import type { UnitType } from '@r6digital/rapidstor-sdk/domain';
import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import { useSite } from '../../sdk/useSite';

/** Class names this component consumes; a CSS module object satisfies the index signature. */
export interface UnitTypeMetaClasses {
    [key: string]: string;
}

export interface UnitTypeMetaProps {
    unitType: UnitType;
    location: Location;
    classes: UnitTypeMetaClasses;
    hideLevel?: boolean;
}

/**
 * The area / size / type / level strip on a unit type card.
 *
 * Every value comes off the domain object — nothing here reads the settings blob or threads a
 * `location` into a helper.
 */
function UnitTypeMeta({
    unitType, location, classes, hideLevel,
}: UnitTypeMetaProps) {
    const { t } = useTranslation();
    const display = useSite(location).settings.display();

    return (
        <div className={classes.features}>
            <div className={classes.block}>
                <span className={classes.label}>{display.areaSizeSymbol()}</span>
                <span className={classes.value}>
                    {unitType.formatArea({ basis: 'footprint', showSymbol: false })}
                </span>
            </div>
            <div className={classes.block}>
                <span className={classes.label}>
                    {t('standardUnitListing:unitType:size')}
                </span>
                <span className={classes.value}>
                    {unitType.formatDimensions({ supplementary: true })}
                </span>
            </div>
            <div className={classes.block}>
                <span className={classes.label}>
                    {t('standardUnitListing:unitType:type')}
                </span>
                <span className={classes.value}>{unitType.typeName}</span>
            </div>
            {!hideLevel && (
                <div className={classes.block}>
                    <span className={classes.label}>
                        {t('standardUnitListing:unitType:level')}
                    </span>
                    <span className={classes.value}>{unitType.floorLabel}</span>
                </div>
            )}
        </div>
    );
}

export default UnitTypeMeta;
